import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getMyGamificationProfile, getMyProfile, generateAvatar } from '../api/user.api';
import { ArrowLeft, Award, TrendingUp, Star, Trophy, Shield, Check, Loader } from 'lucide-react';
import apiClient from '../api/apiClient';

export default function Profile() {
    const navigate = useNavigate();
    const { currentUser } = useAuth();
    // Removed activeTab state as we are moving to a split layout
    const [profileData, setProfileData] = useState(null);
    const [gamificationData, setGamificationData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [avatarLoading, setAvatarLoading] = useState(false);
    const fileInputRef = useRef(null);

    useEffect(() => {
        const fetchProfileData = async () => {
            try {
                setLoading(true);
                setError(null);

                // First, sync badges to ensure user has all badges they should have earned
                let syncResult = null;
                try {
                    const syncResponse = await apiClient.post('/gamification/sync-badges');
                    syncResult = syncResponse.data;
                    if (syncResult?.success && syncResult?.data?.newBadges?.length > 0) {
                        console.log(`✅ Badges synced! ${syncResult.data.newBadges.length} new badge(s) earned!`);
                    } else {
                        console.log('✅ Badges synced successfully');
                    }
                } catch (syncError) {
                    console.warn('⚠️ Badge sync failed (non-critical):', syncError);
                    // Continue even if sync fails
                }

                // Fetch both user profile and gamification data (refresh after sync)
                const [profileResponse, gamificationResponse] = await Promise.all([
                    getMyProfile().catch(() => null),
                    getMyGamificationProfile().catch(() => null)
                ]);

                if (profileResponse?.success) {
                    setProfileData(profileResponse.data);
                }

                if (gamificationResponse?.success) {
                    setGamificationData(gamificationResponse.data);
                }

                // If new badges were earned, show a notification (optional)
                if (syncResult?.success && syncResult?.data?.newBadges?.length > 0) {
                    // You could show a toast notification here if desired
                    console.log('New badges earned:', syncResult.data.newBadges);
                }
            } catch (err) {
                console.error('Error fetching profile data:', err);
                setError('Failed to load profile data');
            } finally {
                setLoading(false);
            }
        };

        fetchProfileData();
    }, []);

    const handleFileSelect = async (event) => {
        const file = event.target.files[0];
        if (!file) return;
        setAvatarLoading(true);
        try {
            const data = await generateAvatar(file);
            if (!data.success) throw new Error(data.error || 'Failed to generate avatar');
            // Update profile data with new avatar URL
            setProfileData(prev => prev ? { ...prev, avatar_url: data.avatar_url } : { avatar_url: data.avatar_url });
            // Optionally refresh profile data
            const profileResponse = await getMyProfile().catch(() => null);
            if (profileResponse?.success) {
                setProfileData(profileResponse.data);
            }
        } catch (error) {
            console.error('Avatar upload error:', error);
        } finally {
            setAvatarLoading(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto mb-4"></div>
                    <p className="text-gray-400">Loading profile...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center">
                <div className="text-center">
                    <p className="text-red-400 mb-4">{error}</p>
                    <button
                        onClick={() => navigate('/dashboard')}
                        className="px-4 py-2 bg-blue-600 rounded-lg hover:bg-blue-700"
                    >
                        Back to Dashboard
                    </button>
                </div>
            </div>
        );
    }

    const user = profileData || currentUser;
    const gamification = gamificationData;
    const badges = gamification?.badges?.list || [];
    const profile = gamification?.profile || {};

    const userName = user?.first_name && user?.last_name
        ? `${user.first_name} ${user.last_name}`
        : user?.first_name || user?.email?.split('@')[0] || 'User';

    const userEmail = user?.email || '';
    const userLevel = profile?.level || 1;
    const totalXp = profile?.total_xp || 0;
    const badgesCount = badges.length || 0;
    const streakDays = profile?.streak_days || 0;
    const challengesCompleted = profile?.challenges_completed || 0;

    // Format member since date
    const memberSince = user?.created_at
        ? new Date(user.created_at).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })
        : 'N/A';

    return (
        <div
            className="min-h-screen text-white bg-cover bg-center bg-no-repeat bg-fixed"
            style={{ backgroundImage: "linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url('/assets/images/achievement_bg.gif')" }}

        >
            {/* Header */}
            <div className="sticky top-0 z-40 pt-4">
                <button
                    onClick={() => navigate('/dashboard')}
                    className="w-full sm:w-auto flex items-center justify-center gap-2 rounded-lg p-2 text-gray-300 hover:text-white transition-colors duration-200 border border-green-500/30 shadow-[0_0_5px_#0ff,0_0_15px_#0ff,0_0_25px_#0ff] hover:shadow-[0_0_10px_#ff0,0_0_10px_#ff0,0_0_10px_#ffb] bg-gray-900/80 backdrop-blur-sm"
                >
                    <ArrowLeft size={20} className="mr-1" />
                    <span>Back to Dashboard</span>
                </button>
            </div>

            <div className="w-full mx-auto px-4 sm:px-6 py-6 sm:py-8">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
                    {/* Left Column: Badges (Takes up 8 columns) */}
                    <div className="lg:col-span-8 order-2 lg:order-1">
                        <div className="h-full">
                            <h3 className="text-2xl font-bold mb-6 flex items-center">
                                <Award className="mr-3 text-purple-400" />
                                Badges & Achievements
                                <span className="ml-3 text-sm font-normal text-gray-400 bg-gray-700 px-3 py-1 rounded-full">
                                    {badgesCount} Total
                                </span>
                            </h3>

                            {badges.length === 0 ? (
                                <div className="text-center py-12 bg-gray-700/30 rounded-lg border border-gray-700 border-dashed">
                                    <Award size={64} className="text-gray-600 mx-auto mb-4" />
                                    <h4 className="text-lg font-semibold text-gray-300">No achievements unlocked yet</h4>
                                    <p className="text-gray-400 mt-2">Complete challenges to earn your first badge!</p>
                                </div>
                            ) : (
                                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 sm:gap-6">
                                    {badges.map((badge) => {
                                        const getBadgeImage = (name) => {
                                            const normalizedName = name.toLowerCase();
                                            if (normalizedName.includes('phishing awesome')) return 'url("/assets/images/badges/phishing_awesome.jpg")';
                                            if (normalizedName.includes('phishing aware')) return 'url("/assets/images/badges/phishing_aware.png")';
                                            if (normalizedName.includes('veteran')) return 'url("/assets/images/badges/veteran.png")';
                                            if (normalizedName.includes('email security')) return 'url("/assets/images/badges/email-security.png")';
                                            // Fallback for new badges or missing images
                                            return 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)';
                                        };

                                        const badgeBg = getBadgeImage(badge.name);
                                        const isImage = badgeBg.includes('url');

                                        return (
                                            <div
                                                key={badge.id}
                                                className="group relative bg-gray-900/40 backdrop-blur-md rounded-xl border border-white/10 overflow-hidden hover:border-cyan-500/50 transition-all duration-300 hover:shadow-[0_0_30px_rgba(6,182,212,0.15)] hover:-translate-y-1"
                                            >
                                                {/* Ambient Glow */}
                                                <div className="absolute top-0 inset-x-0 h-32 bg-gradient-to-b from-cyan-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />

                                                {/* Image Area */}
                                                <div className="relative h-48 w-full overflow-hidden bg-gray-900">
                                                    {isImage ? (
                                                        <div
                                                            className="absolute inset-0 bg-cover bg-center transform transition-transform duration-700 group-hover:scale-110"
                                                            style={{ backgroundImage: badgeBg }}
                                                        />
                                                    ) : (
                                                        <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-gray-800 to-gray-900">
                                                            <Award size={64} className="text-gray-600 group-hover:text-cyan-400 transition-colors duration-300" />
                                                        </div>
                                                    )}

                                                    {/* Gradient Overlay for Text Readability/Blending */}
                                                    <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/40 to-transparent" />

                                                    {/* Earned Status - Stamped Effect */}
                                                    <div className="absolute top-4 right-2 transform rotate-[15deg] opacity-90 group-hover:opacity-100 transition-all duration-300 z-10">
                                                        <div className="border-4 border-yellow-300 text-yellow-300 font-black text-xs px-3 py-1 rounded-sm uppercase tracking-widest shadow-sm backdrop-blur-[1px] mask-image-grunge">
                                                            CERTIFIED
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Content Area */}
                                                <div className="relative p-5 -mt-12 flex flex-col flex-grow">
                                                    {/* Category Pill */}
                                                    <div className="flex justify-center mb-3">
                                                        <span className="px-3 py-0.5 text-[10px] font-bold uppercase tracking-widest text-cyan-300 bg-cyan-950/50 border border-cyan-500/30 rounded-full backdrop-blur-md shadow-sm">
                                                            {badge.category || 'Achievement'}
                                                        </span>
                                                    </div>

                                                    {/* Title */}
                                                    <h4 className="text-lg font-bold text-center text-white mb-2 leading-tight group-hover:text-cyan-400 transition-colors uppercase tracking-tight shadow-black drop-shadow-md">
                                                        {badge.name}
                                                    </h4>

                                                    {/* Description */}
                                                    <p className="text-gray-400 text-xs text-center line-clamp-3 mb-4 group-hover:text-gray-300 transition-colors leading-relaxed px-2">
                                                        {badge.description}
                                                    </p>

                                                    {/* Footer Date */}
                                                    {badge.awarded_on && (
                                                        <div className="mt-auto border-t border-white/5 pt-3 w-full text-center">
                                                            <p className="text-gray-500 text-[10px] uppercase tracking-wider font-medium group-hover:text-cyan-500/70 transition-colors">
                                                                Acquired on {new Date(badge.awarded_on).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}
                                                            </p>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Right Column: Profile Details (Takes up 4 columns) */}
                    <div className="lg:col-span-4 order-1 lg:order-2 space-y-6">
                        {/* Profile Info Card */}
                        <div className="bg-gray-800 rounded-lg p-6 border border-gray-700 shadow-lg">
                            <div className="flex flex-col items-center">
                                <div className="relative mb-4 group">
                                    <div className="w-32 h-32 rounded-full p-1 border-4 border-blue-500/30 group-hover:border-blue-500 transition-colors">
                                        <img
                                            src={user?.avatar_url || '/assets/images/avatar.png'}
                                            alt="User Avatar"
                                            className="w-full h-full rounded-full object-cover cursor-pointer hover:opacity-80 transition-opacity"
                                            onClick={() => fileInputRef.current?.click()}
                                        />
                                    </div>
                                    {avatarLoading && (
                                        <div className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full m-1">
                                            <Loader className="w-8 h-8 animate-spin text-white" />
                                        </div>
                                    )}
                                    <div className="absolute bottom-2 right-2 bg-blue-600 rounded-full p-2 shadow-lg border-2 border-gray-800 cursor-pointer hover:bg-blue-500 transition-colors" onClick={() => fileInputRef.current?.click()}>
                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21.174 6.812a1 1 0 0 0-3.986-3.987L3.842 16.174a2 2 0 0 0-.5.5l-3 9 9-3a2 2 0 0 0 .5-.5Z" /></svg>
                                    </div>
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept="image/*"
                                        onChange={handleFileSelect}
                                        className="hidden"
                                    />
                                </div>
                                <h2 className="text-2xl font-bold text-white mb-1">{userName}</h2>
                                <p className="text-gray-400 mb-6">{userEmail}</p>

                                <div className="w-full space-y-4">
                                    <div className="flex justify-between items-center p-3 bg-gray-700/50 rounded-lg">
                                        <div className="flex items-center space-x-3">
                                            <div className="p-2 bg-blue-500/20 rounded-lg">
                                                <TrendingUp size={20} className="text-blue-400" />
                                            </div>
                                            <span className="text-gray-300">Level</span>
                                        </div>
                                        <span className="text-xl font-bold text-white">{userLevel}</span>
                                    </div>

                                    <div className="flex justify-between items-center p-3 bg-gray-700/50 rounded-lg">
                                        <div className="flex items-center space-x-3">
                                            <div className="p-2 bg-yellow-500/20 rounded-lg">
                                                <Star size={20} className="text-yellow-400" />
                                            </div>
                                            <span className="text-gray-300">Total XP</span>
                                        </div>
                                        <span className="text-xl font-bold text-white">{totalXp.toLocaleString()}</span>
                                    </div>

                                    <div className="flex justify-between items-center p-3 bg-gray-700/50 rounded-lg">
                                        <div className="flex items-center space-x-3">
                                            <div className="p-2 bg-orange-500/20 rounded-lg">
                                                <Trophy size={20} className="text-orange-400" />
                                            </div>
                                            <span className="text-gray-300">Current Streak</span>
                                        </div>
                                        <span className="text-xl font-bold text-white">{streakDays} Days</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Additional Details Card */}
                        <div className="bg-gray-800 rounded-lg p-6 border border-gray-700 shadow-lg">
                            <h3 className="text-lg font-bold mb-4 flex items-center text-gray-200">
                                <Shield className="mr-2 text-green-400" size={18} />
                                Profile Details
                            </h3>
                            <div className="space-y-4">
                                <div className="border-b border-gray-700 pb-3 last:border-0 last:pb-0">
                                    <p className="text-sm text-gray-500 mb-1">Role</p>
                                    <p className="text-white font-medium">{user?.role_name || 'EndUser'}</p>
                                </div>
                                <div className="border-b border-gray-700 pb-3 last:border-0 last:pb-0">
                                    <p className="text-sm text-gray-500 mb-1">Department</p>
                                    <p className="text-white font-medium">{user?.department_name || 'Not assigned'}</p>
                                </div>
                                <div className="border-b border-gray-700 pb-3 last:border-0 last:pb-0">
                                    <p className="text-sm text-gray-500 mb-1">Total Challenges</p>
                                    <p className="text-white font-medium">{challengesCompleted}</p>
                                </div>
                                <div className="border-b border-gray-700 pb-3 last:border-0 last:pb-0">
                                    <p className="text-sm text-gray-500 mb-1">Member Since</p>
                                    <p className="text-white font-medium">{memberSince}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
